// ── Overwatch Review Panel ── Full-screen overlay for the Overwatch case review system.
// Fetches from /api/overwatch/queue, /case, /verdict, /stats endpoints.
// Allows investigators to review reported players and submit verdicts.

import { getToken } from '../auth/token-store';
import { isMobile } from '../core/device';

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';
const GOLD = '#e8b849';

// ── Types matching server JSON shapes ──

export interface OverwatchCase {
  id: string;
  suspect_id: string;
  match_id: string;
  reporter_ids: string[];
  status: string;
  created_at: string;
  assigned_to?: string;
}

export interface OverwatchStats {
  cases_reviewed: number;
  accuracy: number;
  verdicts_submitted: number;
}

export type VerdictLevel = 'innocent' | 'minor_disruption' | 'major_disruption' | 'cheating' | 'inconclusive';

type OverwatchView = 'queue' | 'case' | 'stats';
type CaseFilter = 'all' | 'open' | 'closed' | 'mine';

export class OverwatchPanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;
  private apiBaseUrl: string;
  private reviewerId: string;
  private onBack: (() => void) | null = null;
  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);

  // Data
  private caseQueue: OverwatchCase[] = [];
  private activeCase: OverwatchCase | null = null;
  private stats: OverwatchStats | null = null;
  private view: OverwatchView = 'queue';
  private errorMessage = '';
  private selectedVerdict: VerdictLevel | null = null;
  private caseFilter: CaseFilter = 'all';

  // U-H-005: 10-second review timer. SUBMIT VERDICT stays disabled until the
  // reviewer has spent at least this many ms inspecting the case, so verdicts
  // (which carry real trust-score consequences) can't be speed-clicked.
  private static readonly VERDICT_REVIEW_MS = 10_000;
  private caseLoadedAt = 0;
  private reviewTimerHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(apiBaseUrl: string, reviewerId = '') {
    this.apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    this.reviewerId = reviewerId;

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '59',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      width: 'min(700px, calc(100vw - 24px))',
      maxHeight: '85vh',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });
    this.overlay.appendChild(this.contentEl);

    // Click outside to close — F-UI-014: do not discard a selected verdict on
    // background misclick; reviewer must explicitly cancel via the close button.
    this.overlay.addEventListener('click', (e) => {
      if (e.target !== this.overlay) return;
      if (this.selectedVerdict !== null) return;
      this.hide();
    });

    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  setReviewerId(id: string): void {
    this.reviewerId = id;
  }

  setOnBack(callback: (() => void) | null): void {
    this.onBack = callback;
  }

  getErrorMessage(): string {
    return this.errorMessage;
  }

  async show(): Promise<void> {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.errorMessage = '';
    this.view = 'queue';
    await this.loadQueue();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    this.onBack?.();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    document.removeEventListener('keydown', this.boundKeyHandler);
    if (this.reviewTimerHandle !== null) {
      clearTimeout(this.reviewTimerHandle);
      this.reviewTimerHandle = null;
    }
    this.overlay.remove();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
      return;
    }

    const hotkeyVerdict = this.getVerdictHotkey(e);
    if (hotkeyVerdict) {
      e.preventDefault();
      e.stopPropagation();
      this.selectedVerdict = hotkeyVerdict;
      this.render();
    }
  }

  private isDesktop(): boolean {
    try {
      return !isMobile();
    } catch {
      return true;
    }
  }

  private getVerdictHotkey(e: KeyboardEvent): VerdictLevel | null {
    if (!this.visible || this.view !== 'case' || !this.activeCase || !this.isDesktop()) return null;
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.repeat) return null;
    if (e.key === '1') return 'innocent';
    if (e.key === '2') return 'cheating';
    if (e.key === '3') return 'inconclusive';
    return null;
  }

  // ── API helpers ──

  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private async apiFetch(path: string, init?: RequestInit): Promise<Response> {
    return fetch(`${this.apiBaseUrl}${path}`, {
      ...init,
      headers: { ...this.authHeaders(), ...(init?.headers ?? {}) },
    });
  }

  // Reads the server's JSON `{"error": "..."}` body, falling back to a generic
  // status-code message when the body is missing or malformed.
  private async extractServerError(res: Response, fallback: string): Promise<string> {
    try {
      const data = await res.json();
      const msg = (data as { error?: string })?.error;
      if (msg && typeof msg === 'string') return msg;
    } catch {
      // body wasn't JSON
    }
    return `${fallback} (HTTP ${res.status})`;
  }

  // ── Data loaders ──

  private async loadQueue(): Promise<void> {
    try {
      const res = await this.apiFetch('/api/overwatch/queue');
      if (!res.ok) {
        this.caseQueue = [];
        this.errorMessage = await this.extractServerError(res, 'Failed to load case queue');
        this.view = 'queue';
        this.render();
        return;
      }
      const data = await res.json();
      this.caseQueue = Array.isArray(data) ? data : (data.cases ?? []);
      this.errorMessage = '';
    } catch (err) {
      console.error('[OverwatchPanel] Failed to load queue:', err);
      this.caseQueue = [];
      this.errorMessage = 'Failed to load case queue (network error)';
    }
    this.view = 'queue';
    this.render();
  }

  private async loadCase(): Promise<void> {
    // Server resolves the reviewer from the JWT auth context, not the query
    // string — so we don't gate on a populated reviewerId here. The query
    // param is sent for telemetry / fallback paths but is not authoritative.
    try {
      const res = await this.apiFetch(
        `/api/overwatch/case?reviewer_id=${encodeURIComponent(this.reviewerId || 'me')}`,
      );
      if (!res.ok) {
        this.activeCase = null;
        this.errorMessage = await this.extractServerError(res, 'No cases available');
        this.view = 'case';
        this.render();
        return;
      }
      const data = await res.json();
      this.activeCase = data;
      this.selectedVerdict = null;
      this.errorMessage = '';
      this.startReviewTimer();
    } catch (err) {
      console.error('[OverwatchPanel] Failed to load case:', err);
      this.activeCase = null;
      this.errorMessage = 'Network error loading case';
    }
    this.view = 'case';
    this.render();
  }

  // U-H-005: arm the 10s review timer for the freshly-loaded case. When it
  // fires, re-render so SUBMIT VERDICT becomes enabled.
  private startReviewTimer(): void {
    if (this.reviewTimerHandle !== null) {
      clearTimeout(this.reviewTimerHandle);
      this.reviewTimerHandle = null;
    }
    this.caseLoadedAt = Date.now();
    this.reviewTimerHandle = setTimeout(() => {
      this.reviewTimerHandle = null;
      if (this.visible && this.view === 'case') this.render();
    }, OverwatchPanel.VERDICT_REVIEW_MS);
  }

  /** U-H-005: true once the reviewer has spent the minimum review window
   *  inspecting the loaded case. Used to gate SUBMIT VERDICT. */
  isVerdictReady(): boolean {
    if (!this.activeCase) return false;
    return Date.now() - this.caseLoadedAt >= OverwatchPanel.VERDICT_REVIEW_MS;
  }

  /** U-H-005: ms remaining before SUBMIT VERDICT becomes available. */
  getReviewTimerRemainingMs(): number {
    if (!this.activeCase) return 0;
    const elapsed = Date.now() - this.caseLoadedAt;
    return Math.max(0, OverwatchPanel.VERDICT_REVIEW_MS - elapsed);
  }

  private async submitVerdict(): Promise<void> {
    if (!this.activeCase || !this.selectedVerdict || !this.reviewerId) return;
    // U-H-005: belt-and-suspenders — the button is disabled before the timer
    // elapses, but a programmatic caller could still hit this. Refuse early.
    if (!this.isVerdictReady()) return;

    const verdictMap: Record<VerdictLevel, { aim: string; vision: string; other: string; griefing: boolean }> = {
      innocent: { aim: 'innocent', vision: 'innocent', other: 'innocent', griefing: false },
      minor_disruption: { aim: 'innocent', vision: 'innocent', other: 'suspicious', griefing: true },
      major_disruption: { aim: 'innocent', vision: 'innocent', other: 'evident', griefing: true },
      cheating: { aim: 'evident', vision: 'evident', other: 'innocent', griefing: false },
      inconclusive: { aim: 'insufficient', vision: 'insufficient', other: 'insufficient', griefing: false },
    };

    const v = verdictMap[this.selectedVerdict];

    try {
      const res = await this.apiFetch('/api/overwatch/verdict', {
        method: 'POST',
        body: JSON.stringify({
          reviewer_id: this.reviewerId,
          case_id: this.activeCase.id,
          aim: v.aim,
          vision: v.vision,
          other: v.other,
          griefing: v.griefing,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Submit failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Submit failed';
        this.render();
        return;
      }
      this.errorMessage = 'Verdict submitted successfully';
      this.activeCase = null;
      this.selectedVerdict = null;
      await this.loadQueue();
    } catch {
      this.errorMessage = 'Network error submitting verdict';
      this.render();
    }
  }

  private async loadStats(): Promise<void> {
    if (!this.reviewerId) {
      this.errorMessage = 'Reviewer ID not set';
      this.render();
      return;
    }
    try {
      const res = await this.apiFetch(
        `/api/overwatch/stats?reviewer_id=${encodeURIComponent(this.reviewerId)}`,
      );
      if (!res.ok) {
        this.stats = null;
        this.errorMessage = await this.extractServerError(res, 'Failed to load reviewer stats');
        this.view = 'stats';
        this.render();
        return;
      }
      this.stats = await res.json();
      this.errorMessage = '';
    } catch (err) {
      console.error('[OverwatchPanel] Failed to load stats:', err);
      this.stats = null;
      this.errorMessage = 'Network error loading reviewer stats';
    }
    this.view = 'stats';
    this.render();
  }

  // ── Rendering ──

  private render(): void {
    this.contentEl.innerHTML = '';

    // Header
    const header = this.createHeader();
    this.contentEl.appendChild(header);

    // Error bar
    if (this.errorMessage) {
      const errBar = document.createElement('div');
      Object.assign(errBar.style, {
        padding: '8px 16px',
        background: this.errorMessage.includes('success') ? 'rgba(76,175,80,0.15)' : 'rgba(233,69,96,0.15)',
        color: this.errorMessage.includes('success') ? '#4caf50' : ACCENT,
        fontSize: '12px',
        letterSpacing: '1px',
      });
      errBar.textContent = this.errorMessage;
      this.contentEl.appendChild(errBar);
    }

    // Body
    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1',
      overflowY: 'auto',
      padding: '16px',
    });

    switch (this.view) {
      case 'queue':
        this.renderQueueView(body);
        break;
      case 'case':
        this.renderCaseView(body);
        break;
      case 'stats':
        this.renderStatsView(body);
        break;
    }

    this.contentEl.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '12px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = this.view === 'case' && this.activeCase && this.isDesktop()
      ? '[ESC] Close | Hotkeys: 1 Innocent / 2 Guilty / 3 Inconclusive'
      : '[ESC] Close';
    this.contentEl.appendChild(footer);
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '20px 24px 14px',
      borderBottom: `1px solid ${ACCENT}30`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    });

    const left = document.createElement('div');
    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      fontSize: '22px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
    });
    titleEl.textContent = 'OVERWATCH';
    left.appendChild(titleEl);

    const subEl = document.createElement('div');
    Object.assign(subEl.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
      marginTop: '4px',
    });
    subEl.textContent = this.view === 'queue'
      ? 'Case queue'
      : this.view === 'case'
        ? 'Review evidence'
        : 'Your statistics';
    left.appendChild(subEl);
    header.appendChild(left);

    // Nav
    const nav = document.createElement('div');
    Object.assign(nav.style, { display: 'flex', gap: '8px' });

    if (this.view !== 'queue') {
      const backBtn = this.createBtn('QUEUE', () => { void this.loadQueue(); });
      nav.appendChild(backBtn);
    }

    const caseBtn = this.createBtn('NEXT CASE', () => { void this.loadCase(); });
    caseBtn.style.background = 'rgba(233,69,96,0.15)';
    caseBtn.style.borderColor = `${ACCENT}60`;
    nav.appendChild(caseBtn);

    const statsBtn = this.createBtn('STATS', () => { void this.loadStats(); });
    nav.appendChild(statsBtn);

    const closeBtn = this.createBtn('CLOSE', () => this.hide());
    nav.appendChild(closeBtn);

    header.appendChild(nav);
    return header;
  }

  private createBtn(text: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    Object.assign(btn.style, {
      padding: '8px 14px',
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '3px',
      color: '#d2d2cb',
      cursor: 'pointer',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '11px',
      fontFamily: FONT,
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = `${ACCENT}90`;
      btn.style.color = '#fff';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.14)';
      btn.style.color = '#d2d2cb';
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ── Queue view ──

  private renderQueueView(container: HTMLElement): void {
    const filteredCases = this.getFilteredCases();

    // Queue count
    const countEl = document.createElement('div');
    Object.assign(countEl.style, {
      fontSize: '14px',
      color: GOLD,
      fontWeight: 'bold',
      letterSpacing: '2px',
      marginBottom: '12px',
    });
    countEl.textContent = `PENDING CASES: ${filteredCases.length}/${this.caseQueue.length}`;
    container.appendChild(countEl);
    this.renderCaseFilters(container);

    if (filteredCases.length === 0) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        textAlign: 'center',
        padding: '40px',
        fontSize: '13px',
      });
      empty.textContent = this.caseQueue.length === 0 ? 'No cases in queue. Check back later.' : 'No cases match this filter.';
      container.appendChild(empty);
      return;
    }

    // Column headers
    const colHeader = document.createElement('div');
    Object.assign(colHeader.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 120px 120px 80px',
      padding: '8px 12px',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    });
    colHeader.innerHTML = '<div>CASE ID</div><div>SUSPECT</div><div>STATUS</div><div>REPORTS</div>';
    container.appendChild(colHeader);

    for (const c of filteredCases) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '1fr 120px 120px 80px',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
      });

      const idEl = document.createElement('div');
      idEl.style.color = '#ccc';
      idEl.textContent = c.id.length > 12 ? c.id.slice(0, 8) + '...' : c.id;
      row.appendChild(idEl);

      const suspectEl = document.createElement('div');
      suspectEl.style.color = '#fff';
      suspectEl.textContent = c.suspect_id.length > 10 ? c.suspect_id.slice(0, 8) + '...' : c.suspect_id;
      row.appendChild(suspectEl);

      const statusEl = document.createElement('div');
      statusEl.style.color = c.status === 'pending' ? GOLD : '#888';
      statusEl.style.fontSize = '11px';
      statusEl.style.textTransform = 'uppercase';
      statusEl.textContent = c.status;
      row.appendChild(statusEl);

      const reportsEl = document.createElement('div');
      reportsEl.style.color = '#888';
      reportsEl.style.textAlign = 'center';
      reportsEl.textContent = String(c.reporter_ids?.length ?? 0);
      row.appendChild(reportsEl);

      container.appendChild(row);
    }
  }

  private renderCaseFilters(container: HTMLElement): void {
    const filters: Array<{ id: CaseFilter; label: string }> = [
      { id: 'all', label: 'All' },
      { id: 'open', label: 'Open' },
      { id: 'closed', label: 'Closed' },
      { id: 'mine', label: 'My Cases' },
    ];

    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginBottom: '14px',
    });

    for (const filter of filters) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = filter.label;
      const active = this.caseFilter === filter.id;
      Object.assign(btn.style, {
        padding: '6px 12px',
        background: active ? 'rgba(233,69,96,0.18)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${active ? `${ACCENT}70` : 'rgba(255,255,255,0.14)'}`,
        borderRadius: '3px',
        color: active ? '#fff' : '#d2d2cb',
        cursor: 'pointer',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        fontSize: '10px',
        fontFamily: FONT,
      });
      btn.addEventListener('click', () => {
        this.caseFilter = filter.id;
        this.render();
      });
      row.appendChild(btn);
    }

    container.appendChild(row);
  }

  private getFilteredCases(): OverwatchCase[] {
    switch (this.caseFilter) {
      case 'open':
        return this.caseQueue.filter((c) => this.isOpenCase(c));
      case 'closed':
        return this.caseQueue.filter((c) => this.isClosedCase(c));
      case 'mine':
        return this.caseQueue.filter((c) => c.assigned_to !== undefined && c.assigned_to === this.reviewerId);
      case 'all':
      default:
        return this.caseQueue;
    }
  }

  private isOpenCase(c: OverwatchCase): boolean {
    const status = c.status.toLowerCase();
    return status === 'pending' || status === 'open' || status === 'assigned' || status === 'reviewing';
  }

  private isClosedCase(c: OverwatchCase): boolean {
    const status = c.status.toLowerCase();
    return status === 'closed' || status === 'completed' || status === 'resolved';
  }

  // ── Case view ──

  private renderCaseView(container: HTMLElement): void {
    if (!this.activeCase) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        textAlign: 'center',
        padding: '40px',
        fontSize: '13px',
      });
      empty.textContent = 'No case loaded. Click "NEXT CASE" to begin reviewing.';
      container.appendChild(empty);
      return;
    }

    const c = this.activeCase;

    // Case info card
    const infoCard = document.createElement('div');
    Object.assign(infoCard.style, {
      padding: '16px',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '5px',
      background: 'rgba(255,255,255,0.03)',
      marginBottom: '16px',
    });

    const caseTitle = document.createElement('div');
    Object.assign(caseTitle.style, {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#fff',
      marginBottom: '8px',
    });
    caseTitle.textContent = `Case: ${c.id}`;
    infoCard.appendChild(caseTitle);

    const metaLines = [
      `Suspect: ${c.suspect_id}`,
      `Match: ${c.match_id}`,
      `Reports: ${c.reporter_ids?.length ?? 0}`,
      `Status: ${c.status}`,
    ];
    for (const line of metaLines) {
      const metaEl = document.createElement('div');
      metaEl.style.fontSize = '12px';
      metaEl.style.color = '#888';
      metaEl.style.marginBottom = '2px';
      metaEl.textContent = line;
      infoCard.appendChild(metaEl);
    }

    container.appendChild(infoCard);

    // U-H-005: Evidence card now surfaces the concrete match ID + a link to
    // the GOTV demo download endpoint (`/api/demos/{matchID}`, served by
    // gameserver/main.go behind requireAuth) so reviewers have something to
    // act on before submitting a verdict.
    const evidenceCard = document.createElement('div');
    Object.assign(evidenceCard.style, {
      padding: '20px',
      border: `1px dashed ${ACCENT}40`,
      borderRadius: '5px',
      background: 'rgba(0,0,0,0.2)',
      marginBottom: '16px',
    });

    const evidenceTitle = document.createElement('div');
    Object.assign(evidenceTitle.style, {
      fontSize: '14px',
      fontWeight: 'bold',
      color: GOLD,
      letterSpacing: '2px',
      marginBottom: '12px',
      textAlign: 'center',
    });
    evidenceTitle.textContent = 'EVIDENCE';
    evidenceCard.appendChild(evidenceTitle);

    const matchRow = document.createElement('div');
    Object.assign(matchRow.style, {
      fontSize: '12px',
      color: '#ccc',
      marginBottom: '8px',
    });
    matchRow.textContent = `Match ID: ${c.match_id}`;
    evidenceCard.appendChild(matchRow);

    const demoLink = document.createElement('a');
    demoLink.href = `${this.apiBaseUrl}/api/demos/${encodeURIComponent(c.match_id)}`;
    demoLink.textContent = 'Download GOTV demo';
    demoLink.target = '_blank';
    demoLink.rel = 'noopener';
    Object.assign(demoLink.style, {
      display: 'inline-block',
      fontSize: '12px',
      color: ACCENT,
      textDecoration: 'underline',
      marginBottom: '8px',
    });
    evidenceCard.appendChild(demoLink);

    const evidenceHint = document.createElement('div');
    evidenceHint.style.color = '#666';
    evidenceHint.style.fontSize = '11px';
    evidenceHint.style.marginTop = '8px';
    evidenceHint.textContent = 'Review the suspect\'s demo before submitting a verdict.';
    evidenceCard.appendChild(evidenceHint);

    container.appendChild(evidenceCard);

    // Verdict controls
    const verdictSection = document.createElement('div');
    Object.assign(verdictSection.style, {
      marginBottom: '16px',
    });

    const verdictTitle = document.createElement('div');
    Object.assign(verdictTitle.style, {
      fontSize: '13px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '10px',
    });
    verdictTitle.textContent = 'YOUR VERDICT';
    verdictSection.appendChild(verdictTitle);

    // BUG-P-13: Demo review requires watching a GOTV demo, which is desktop-only.
    // On mobile/tablet, replace the verdict buttons with a notice instead.
    // Guard against environments (e.g. headless tests) where `window` is not
    // defined — fall through to the desktop verdict UI in that case.
    let mobile = false;
    try {
      mobile = isMobile();
    } catch {
      mobile = false;
    }
    if (mobile) {
      const notice = document.createElement('div');
      notice.setAttribute('data-role', 'mobile-verdict-notice');
      Object.assign(notice.style, {
        padding: '14px 16px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px dashed rgba(255,255,255,0.18)',
        borderRadius: '4px',
        color: '#aaa',
        fontSize: '12px',
        letterSpacing: '1px',
        textAlign: 'center',
        lineHeight: '1.5',
      });
      notice.textContent = 'Demo review is desktop-only';
      verdictSection.appendChild(notice);
      container.appendChild(verdictSection);
      return;
    }

    const verdictBtnRow = document.createElement('div');
    Object.assign(verdictBtnRow.style, {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    });

    const verdictOptions: Array<{ label: string; value: VerdictLevel; color: string }> = [
      { label: 'Innocent', value: 'innocent', color: '#4caf50' },
      { label: 'Minor Disruption', value: 'minor_disruption', color: GOLD },
      { label: 'Major Disruption', value: 'major_disruption', color: '#ff9800' },
      { label: 'Cheating', value: 'cheating', color: '#ff5252' },
      { label: 'Inconclusive', value: 'inconclusive', color: '#9e9e9e' },
    ];

    for (const opt of verdictOptions) {
      const btn = document.createElement('button');
      btn.textContent = opt.label;
      const isSelected = this.selectedVerdict === opt.value;
      Object.assign(btn.style, {
        padding: '10px 16px',
        background: isSelected ? `${opt.color}30` : 'rgba(255,255,255,0.06)',
        border: `2px solid ${isSelected ? opt.color : 'rgba(255,255,255,0.14)'}`,
        borderRadius: '4px',
        color: isSelected ? opt.color : '#d2d2cb',
        cursor: 'pointer',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        fontSize: '11px',
        fontFamily: FONT,
        fontWeight: isSelected ? 'bold' : 'normal',
      });
      btn.addEventListener('click', () => {
        this.selectedVerdict = opt.value;
        this.render();
      });
      verdictBtnRow.appendChild(btn);
    }

    verdictSection.appendChild(verdictBtnRow);
    container.appendChild(verdictSection);

    // Submit button — U-H-005: gated on the 10s review timer so verdicts
    // (which carry real trust-score consequences) can't be speed-clicked.
    if (this.selectedVerdict) {
      const ready = this.isVerdictReady();
      const remainingSec = Math.ceil(this.getReviewTimerRemainingMs() / 1000);
      const label = ready ? 'SUBMIT VERDICT' : `SUBMIT VERDICT (${remainingSec}s)`;
      const submitBtn = document.createElement('button');
      submitBtn.textContent = label;
      submitBtn.setAttribute('data-role', 'submit-verdict');
      Object.assign(submitBtn.style, {
        padding: '12px 24px',
        background: ready ? 'rgba(233,69,96,0.15)' : 'rgba(80,80,80,0.2)',
        border: `1px solid ${ready ? `${ACCENT}60` : 'rgba(255,255,255,0.14)'}`,
        borderRadius: '3px',
        color: ready ? '#fff' : '#888',
        cursor: ready ? 'pointer' : 'not-allowed',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        fontSize: '13px',
        fontFamily: FONT,
        opacity: ready ? '1' : '0.6',
      });
      if (ready) {
        submitBtn.addEventListener('click', () => { void this.submitVerdict(); });
      } else {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.title = `Review the case for ${remainingSec}s before submitting`;
      }
      container.appendChild(submitBtn);
    }
  }

  // ── Stats view ──

  private renderStatsView(container: HTMLElement): void {
    if (!this.stats) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        textAlign: 'center',
        padding: '40px',
        fontSize: '13px',
      });
      empty.textContent = 'No stats available.';
      container.appendChild(empty);
      return;
    }

    const s = this.stats;

    const statsCard = document.createElement('div');
    Object.assign(statsCard.style, {
      padding: '24px',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '5px',
      background: 'rgba(255,255,255,0.03)',
    });

    const statsTitle = document.createElement('div');
    Object.assign(statsTitle.style, {
      fontSize: '16px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '2px',
      marginBottom: '16px',
    });
    statsTitle.textContent = 'REVIEWER STATISTICS';
    statsCard.appendChild(statsTitle);

    // F-UI-005: Coerce server fields defensively — backend may omit accuracy
    // for fresh reviewers, which would render "NaN%" or crash .toFixed.
    const cases = Number(s.cases_reviewed ?? 0);
    const verdicts = Number(s.verdicts_submitted ?? 0);
    const accuracy = Number(s.accuracy ?? 0);
    const statEntries: Array<{ label: string; value: string; color: string }> = [
      { label: 'Cases Reviewed', value: String(cases), color: '#fff' },
      { label: 'Verdicts Submitted', value: String(verdicts), color: '#fff' },
      { label: 'Accuracy', value: `${(accuracy * 100).toFixed(1)}%`, color: accuracy >= 0.8 ? '#4caf50' : GOLD },
    ];

    for (const entry of statEntries) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      });

      const label = document.createElement('div');
      label.style.color = '#888';
      label.style.fontSize = '13px';
      label.textContent = entry.label;
      row.appendChild(label);

      const value = document.createElement('div');
      value.style.color = entry.color;
      value.style.fontSize = '15px';
      value.style.fontWeight = 'bold';
      value.textContent = entry.value;
      row.appendChild(value);

      statsCard.appendChild(row);
    }

    container.appendChild(statsCard);
  }
}
